import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import {
  StyledOrderLink,
  StyledOrderCardRightHeader,
  StyledOrderNumber,
  StyledSubStageEyeBrow,
} from '../index.style';
import {
  useMediaQuery,
  Paper as MuiPaper,
  Avatar,
  Menu,
  Theme,
  Tooltip,
  Grid,
  Box,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import DefaultUptimeTheme from 'component/theme/defaultTheme';
import EyeBrow from '../../eyeBrow/eyeBrow';
import { IconButton } from '../../ButtonComponents';
import { getAvatarName } from 'utils/functions';
import ErrorIcon from '@mui/icons-material/Error';
import {
  ORDER_STATUS,
  APPROVAL_STATUS,
  ORDER_ITEM_STATUS,
} from '../../OrderRequestDetail/constant';
import FieldView from './FieldView';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ApprovalIcon } from 'component/commonComponents/Icon';
import { mapOrderStatus } from 'component/MarketPlace/OrderItemCard/utils';
import MenuContent from './MenuContent';
import useMenu from '../hooks/useMenu';
import ProductList from './ProductList';
import { StyledTooltip } from 'component/cardComponent/pcCard/pcCard.style';

type Props = {
  content: Record<string, any>;
  currentUser?: Record<string, any>;
  isApprovalPage?: boolean;
  onViewDetails?: (orderedId: string) => void;
  onCancelOrder?: (orderItem: Record<string, any>) => void;
  isShowLabelTicket?: boolean;
  labelTicket?: string;
  isShowApproval?: boolean;
  isShowOrder?: boolean;
  onDecline?: (orderedId: string) => void;
  onApprove?: (orderedId: string) => void;
  locale?: Record<string, any>;
  statusTicketLocale?: string;
  stageTicketLocale?: string;
  pendingApprovalDelegatedLocale?: string;
  updatedDate: string;
};

const statusStageText = {
  [ORDER_ITEM_STATUS.PENDING]: 'OPEN',
  [ORDER_ITEM_STATUS.PENDING_APPROVAL]: 'OPEN',
  [ORDER_ITEM_STATUS.SHIPPED]: 'CLOSED',
  [ORDER_ITEM_STATUS.NOT_APPROVED]: 'CLOSED',
  [ORDER_ITEM_STATUS.APPROVED]: 'CLOSED',
  [ORDER_ITEM_STATUS.CANCELED]: 'CLOSED',
};

const APPROVED_TEXT = {
  [ORDER_ITEM_STATUS.NOT_APPROVED]: 'CLOSED',
  [ORDER_ITEM_STATUS.APPROVED]: 'APPROVED',
};

const stageColors = {
  OPEN: {
    background: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.open.background,
    color: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.open.color,
  },
  [ORDER_ITEM_STATUS.PENDING]: {
    background: '#68BC46',
    color: '#ffffff',
  },
  [ORDER_ITEM_STATUS.PENDING_APPROVAL]: {
    background: '#68BC46',
    color: '#ffffff',
  },

  CLOSED: {
    background: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.background,
    color: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.color,
  },
  [ORDER_ITEM_STATUS.SHIPPED]: {
    background: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.background,
    color: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.color,
  },
  [ORDER_ITEM_STATUS.NOT_APPROVED]: {
    background: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.background,
    color: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.color,
  },
  [ORDER_ITEM_STATUS.APPROVED]: {
    background: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.background,
    color: DefaultUptimeTheme.uptimeColors.ticketColors.eyeBrow.approved.color,
  },
};

const Label = ({ children }: { children: React.ReactNode }) => {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      fontWeight={700}
      lineHeight="32px"
      textTransform="uppercase"
    >
      {children}
    </Typography>
  );
};

const ApprovalCard = (props: Props) => {
  const {
    content,
    currentUser,
    isApprovalPage = true,
    isShowLabelTicket = false,
    labelTicket,
    locale,
    statusTicketLocale,
    pendingApprovalDelegatedLocale,
    updatedDate,
    onViewDetails = () => {},
    onCancelOrder = () => {},
    onApprove = () => {},
    onDecline = () => {},
  } = props;
  const theme = useTheme() as typeof DefaultUptimeTheme;
  const isMdUp = useMediaQuery((theme: Theme) => 
    theme.breakpoints.up('md')
  );
  const isMdDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );
  const {
    open: openActionMenu,
    anchorEl,
    setAnchorEl,
    onClickMenu,
    onCloseMenu,
  } = useMenu();

  const [isHover, setIsHover] = useState(false);

  const orderDetailsStatus =
    content?.approval?.approver_type === APPROVAL_STATUS.DELEGATED
      ? content?.order_detail?.meta_status?.status +
        `_${APPROVAL_STATUS.DELEGATED}`
      : content?.order_detail?.meta_status?.status ||
        content?.order_detail?.status;
  const { statusText } = mapOrderStatus(orderDetailsStatus);

  const createdOn = content?.order_detail?.createdOn;
  const products = content?.order_detail?.products;
  const approvedOn = content?.approval?.approved_on;
  const requester =
    content?.requested_by?.name || content?.name || currentUser?.name;
  const approvalStatus =
    locale?.commonStatus?.[APPROVED_TEXT?.[content?.approval?.approval_status]];
  const isPendingApprovalCancelled = (content?.approval?.approval_status === ORDER_ITEM_STATUS.PENDING || content?.approval?.approval_status === ORDER_ITEM_STATUS.PENDING_APPROVAL) &&
    (orderDetailsStatus === ORDER_STATUS.CANCELED);
  const showMoreActionButton =
    orderDetailsStatus === ORDER_STATUS.PENDING_APPROVAL ||
    orderDetailsStatus === ORDER_STATUS.PENDING_APPROVAL_DELEGATED;

  return (
    <MuiPaper
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      variant={isHover ? 'elevation' : 'outlined'}
      elevation={isHover ? 4 : 0}
      sx={{
        borderRadius: '8px',
        padding: '20px 0px',
        '&:hover': { border: 1, borderColor: 'transparent' },
      }}
      data-ritm_number={content?._id}
    >
      {isShowLabelTicket && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          px={4}
        >
          <Label>{labelTicket}</Label>
        </Box>
      )}

      <Grid container pl={2} pb={2}>
        <Grid
          item
          display="flex"
          justifyContent="space-between"
          xs={12}
          sm={12}
          md={4}
          alignItems="center"
          gap={2}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            marginLeft="10px"
          >
            <ApprovalIcon color={theme.uptimeColors.generalColors.icon.enabled} />
            <StyledTooltip title={'Order'} placement="bottom-start">
              <StyledOrderLink
                onClick={() => onViewDetails(content?._id)}
                sx={{ flex: '1', '&:hover': {textDecoration: 'underline !important'}}}
                id="lnk-approvalcard-title_view_detail"
                role="link"
                tabIndex={0}
                aria-label="View Details"
              >
                <StyledOrderNumber
                  variant="h4"
                  fontSize={`${theme.uptimeFontSizes.desktop.fz9}px`}
                  fontWeight={600}
                  color='text.primary'
                  lineHeight={`${theme.uptimeLineHeights.desktop.lh9}px`}
                  textAlign="left"
                  maxlines={1}
                >
                  Order
                </StyledOrderNumber>
              </StyledOrderLink>
            </StyledTooltip>
          </Box>
          {isMdDown && showMoreActionButton && (
            <Box>
              <IconButton
                icon={MoreVertIcon}
                aria-label="View More Button"
                value="viewMoreButton"
                onClick={onClickMenu}
                id="btn-approvalcard-more"
              />
              <Menu
                anchorEl={anchorEl}
                open={openActionMenu}
                onClose={onCloseMenu}
                disableScrollLock
              >
                <MenuContent
                  showCancelButton={showMoreActionButton}
                  isApprovalPage={isApprovalPage}
                  onCancelOrder={() => {
                    onCloseMenu();
                    onCancelOrder(content);
                  }}
                  onApprove={() => {
                    onApprove(content?._id);
                    setAnchorEl(null);
                  }}
                  onDecline={() => {
                    onDecline(content?._id);
                    setAnchorEl(null);
                  }}
                  locale={locale}
                />
              </Menu>
            </Box>
          )}
        </Grid>
        <Grid
          item
          display="flex"
          xs={12}
          sm={12}
          md={5}
          alignItems="center"
          justifyContent="center"
        >
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <EyeBrow
              variant="filled"
              sx={{
                backgroundColor: isPendingApprovalCancelled
                  ? stageColors[statusStageText?.[ORDER_ITEM_STATUS.CANCELED]]?.background
                  : stageColors[statusStageText?.[content?.approval?.approval_status]]?.background,
                color: isPendingApprovalCancelled
                  ? stageColors[statusStageText?.[ORDER_ITEM_STATUS.CANCELED]]?.color
                  : stageColors[statusStageText?.[content?.approval?.approval_status]]?.color,
                fontWeight: 600,
                height: `${theme.uptimeSpace[12]}px`,
                lineHeight: `${theme.uptimeSpace[9]}px`,
                fontSize: `${theme.uptimeSpace[6]}px`,
              }}
              label={
                (locale?.commonStatus?.[
                  statusStageText[
                    isPendingApprovalCancelled ? ORDER_ITEM_STATUS.CANCELED : content?.approval?.approval_status
                  ]
                ] ||
                  statusStageText[
                    isPendingApprovalCancelled ? ORDER_ITEM_STATUS.CANCELED : content?.approval?.approval_status
                  ]) +
                  (content?.approval?.approver_type === 'delegated'
                    ? ' - ' + pendingApprovalDelegatedLocale?.toUpperCase()
                    : '') || ''
              }
              id="lbl-approvalcard-status"
              data-ritm_number={content?._id}
            />
          </Box>
          <Box>
            <ChevronRightIcon
              sx={{ width: { xs: '24px', sm: '75px' }, height: '24px' }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <StyledSubStageEyeBrow
              sx={{
                backgroundColor: isPendingApprovalCancelled
                  ? stageColors[statusStageText?.[ORDER_ITEM_STATUS.CANCELED]]?.background
                  : stageColors[content?.approval?.approval_status]?.background,
                color: isPendingApprovalCancelled
                  ? stageColors[statusStageText?.[ORDER_ITEM_STATUS.CANCELED]]?.color
                  : stageColors[content?.approval?.approval_status]?.color,
                padding: '3px 4px',
                fontWeight: 600,
                height: `${theme.uptimeSpace[12]}px`,
                lineHeight: `${theme.uptimeSpace[9]}px`,
                fontSize: `${theme.uptimeSpace[6]}px`,
                whiteSpace: 'unset'
              }}
              label={
                content?.approval?.approver_type === APPROVAL_STATUS.DELEGATED
                  ? ((pendingApprovalDelegatedLocale || '') as string)
                  : ((statusTicketLocale || statusText) as string)
              }
              variant="filled"
              endIcon={
                <Tooltip title={content?.approval?.approver_type === APPROVAL_STATUS.DELEGATED
                  ? ((pendingApprovalDelegatedLocale || '') as string)
                  : ((statusTicketLocale || statusText) as string)}>
                  <Box display="flex">
                    <ErrorIcon
                      sx={{
                        color: '#0000008c',
                        width: `${theme.uptimeSpace[8]}px`,
                        height: `${theme.uptimeSpace[8]}px`,
                      }}
                    />
                  </Box>
                </Tooltip>
              }
              id="lbl-approvalcard-status_delegate"
              data-ritm_number={content?._id} 
            />
          </Box>
        </Grid>
        <Grid
          item
          display="flex"
          justifyContent="flex-end"
          xs={12}
          sm={12}
          md={3}
          alignItems="center"
          rowGap={2}
        >
          <StyledOrderCardRightHeader
            display="flex"
            alignItems="center"
            position="relative"
          >
            <StyledOrderLink
              onClick={() => onViewDetails(content?._id)}
              arial-label="View Details"
              sx={{ marginLeft: '10px' }}
              id="lnk-approvalcard-view_detail_md"
              data-ritm_number={content?._id}
              role="link"
              tabIndex={0}
              aria-label="View Details"
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
                  '&:hover': {
                    color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
                  },
                }}
                fontWeight={600}
                display={{
                  sm: 'none',
                  md: 'block',
                }}
              >
                {locale?.viewDetail}
              </Typography>
            </StyledOrderLink>
            {isMdUp && showMoreActionButton && (
              <>
                <IconButton
                  icon={MoreVertIcon}
                  aria-label="View More Button"
                  value="viewMoreButton"
                  onClick={onClickMenu}
                  id="btn-approvalcard-more"
                />
                <Menu
                  anchorEl={anchorEl}
                  open={openActionMenu}
                  onClose={onCloseMenu}
                  disableScrollLock
                  slotProps={{
                    root: {
                      sx: {
                        zIndex: '1000 !important',
                      }
                    }
                  }}
                >
                  <MenuContent
                    showCancelButton={showMoreActionButton}
                    isApprovalPage={isApprovalPage}
                    onCancelOrder={() => {
                      onCloseMenu();
                      onCancelOrder(content);
                    }}
                    onApprove={() => {
                      onApprove(content?._id);
                      setAnchorEl(null);
                    }}
                    onDecline={() => {
                      onDecline(content?._id);
                      setAnchorEl(null);
                    }}
                    locale={locale}
                  />
                </Menu>
              </>
            )}
            {isMdUp && !showMoreActionButton && (
              <Box px={2}>
              </Box>
            )}
          </StyledOrderCardRightHeader>
        </Grid>
      </Grid>
      <Box
        display="flex"
        mb={2}
        px={2}
        flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
        justifyContent="space-between"
        width="100%"
      >
        <Box
          display="flex"
          gap={{ xs: 0, md: 4 }}
          alignItems="center"
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
          >
            <Box alignSelf={{ xs: 'center', md: 'flex-start' }}>
              <Typography
                fontSize="14px"
                variant="overline"
                color="text.secondary"
                id="lbl-approvalcard-short_description"
              >
                {content?.shortDescription ||
                  content?.requestItem?.shortDescription ||
                  `#${content?._id?.slice(0, 10)}`}
              </Typography>
            </Box>
            <Box display="flex">
              <StyledOrderLink
                onClick={() => onViewDetails(content?._id)}
                arial-label="View Details"
                sx={{ marginLeft: '10px' }}
                id="lnk-approvalcard-view_detail_sm"
                data-ritm_number={content?._id}
                role="link"
                tabIndex={0}
                aria-label="View Details"
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
                    '&:hover': {
                      color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
                    },
                  }}
                  fontWeight={600}
                  display={{
                    sm: 'block',
                    md: 'none',
                  }}
                >
                  {locale?.viewDetail}
                </Typography>
              </StyledOrderLink>
            </Box>
          </Box>
          {requester && isMdUp && (
            <Box>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Box marginRight="10px">
                  <Label>{locale?.requester}</Label>
                </Box>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  height="100%"
                  width="200px"
                  justifyContent="center"
                  >
                  <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                    <Typography variant="caption"
                      id="img-approvalcard-avatar">
                      {getAvatarName(requester)}
                    </Typography>
                  </Avatar>
                  <Typography noWrap variant="body2" color="text.secondary" id="lbl-approvalcard-requester">
                    {requester}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-end"
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
          width={{ xs: '50%', sm: '40%', md: '100%' }}
          alignSelf={{ xs: 'center', sm: 'center' }}
        >
          {requester && isMdDown && (
            <Box>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                alignSelf="flex-start"
              >
                <Box padding="0 10px">
                  <Label>{locale?.requester}</Label>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  height="100%"
                  width={{ xs: "150px", sm: "180px" }}
                  >
                  <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                    <Typography variant="caption"
                      id="img-approvalcard-avatar">
                      {getAvatarName(requester)}
                    </Typography>
                  </Avatar>
                  <Typography noWrap variant="body2" color="text.secondary" id="lbl-approvalcard-requester">
                    {requester}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <FieldView
            label={locale?.created}
            value={moment(createdOn).format('ll')}
          />
          {approvedOn ? (
            <FieldView
              label={approvalStatus || 'N/A'}
              value={moment(approvedOn).format('ll')}
            />
          ) : null}
          <FieldView
            label={locale?.updated}
            value={updatedDate}
          />
        </Box>
      </Box>
      <ProductList products={products} />
    </MuiPaper>
  );
};

export default ApprovalCard;
